/**
 * Minimal API client for the DeployHub v1 API.
 *
 * Responsibilities:
 * - Persist the bearer token in localStorage under `deployhub.token`.
 * - Attach `Authorization: Bearer <token>` and the JSON headers to every call.
 * - Parse RFC-9457 `application/problem+json` errors into a typed {@link ApiError}
 *   (title/detail/field errors/correlation id + `Retry-After`), so the UI can
 *   render field- and form-level messages and throttle notices.
 * - Clear the stored token on a 401 so a stale session cannot linger.
 *
 * No business logic lives here — it is transport only (spec 08 §1).
 */

/** Base path for the v1 API (proxied to the backend in dev and prod). */
const API_BASE = '/api/v1';

/**
 * Read a cookie value by name from `document.cookie`, URL-decoded, or null.
 *
 * Used for the Laravel/Sanctum CSRF handshake: when the API is treated as a
 * stateful (session-cookie) request, the backend sets an `XSRF-TOKEN` cookie and
 * expects it echoed back in the `X-XSRF-TOKEN` header on writes. `fetch` sends the
 * cookie automatically (same-origin) but never mirrors it into the header, so a
 * cookie-bearing POST fails CSRF (419) unless we set the header ourselves.
 */
function readCookie(name: string): string | null {
  if (typeof document === 'undefined') {
    return null;
  }
  const prefix = `${name}=`;
  for (const part of document.cookie.split('; ')) {
    if (part.startsWith(prefix)) {
      return decodeURIComponent(part.slice(prefix.length));
    }
  }
  return null;
}

/** localStorage key holding the bearer token. */
export const TOKEN_STORAGE_KEY = 'deployhub.token';

/** Shape of an RFC-9457 problem document returned by the API. */
export interface ProblemDetails {
  type?: string;
  title?: string;
  status?: number;
  detail?: string;
  /** Field-level validation errors, keyed by field name. */
  errors?: Record<string, string[]>;
  /** Server-generated correlation id for support/debugging. */
  correlation_id?: string;
  /**
   * Set by the login endpoint (as an RFC-9457 extension member) on a 401 when a
   * password is correct but a second factor is still required. The SPA reads this
   * to switch the sign-in form to its MFA-challenge step rather than showing an
   * error (spec 06 §2.1).
   */
  mfa_required?: boolean;
  /**
   * Set alongside {@link mfa_required} on the login 401: the account's active
   * second factor (`'totp'` or `'email_otp'`). The SPA reads it to choose the
   * MFA-challenge UI — an email-OTP user is shown an "we emailed you a code"
   * state and a resend action, a TOTP user the authenticator-app prompt
   * (spec 06 §2.1).
   */
  mfa_method?: string;
}

/** Read the persisted bearer token, or null. Degrades gracefully. */
export function getToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_STORAGE_KEY);
  } catch {
    return null;
  }
}

/** Persist the bearer token. */
export function setToken(token: string): void {
  try {
    localStorage.setItem(TOKEN_STORAGE_KEY, token);
  } catch {
    /* best-effort persistence */
  }
}

/** Remove the persisted bearer token. */
export function clearToken(): void {
  try {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
  } catch {
    /* best-effort */
  }
}

/**
 * A failed API call. Carries the HTTP status, the parsed problem document (when
 * present) and the parsed `Retry-After` (seconds) for throttled responses.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly problem: ProblemDetails | null;
  readonly retryAfterSeconds: number | null;

  constructor(status: number, problem: ProblemDetails | null, retryAfterSeconds: number | null) {
    super(problem?.detail ?? problem?.title ?? `Request failed with status ${status}`);
    this.name = 'ApiError';
    this.status = status;
    this.problem = problem;
    this.retryAfterSeconds = retryAfterSeconds;
  }

  /** First validation message for a field, if any. */
  fieldError(field: string): string | undefined {
    return this.problem?.errors?.[field]?.[0];
  }
}

/** Parse a `Retry-After` header value (delta-seconds) into a number. */
function parseRetryAfter(value: string | null): number | null {
  if (value === null) {
    return null;
  }
  const seconds = Number.parseInt(value, 10);
  return Number.isFinite(seconds) ? seconds : null;
}

/** Options for {@link apiFetch}. */
export interface ApiRequestOptions {
  method?: string;
  /** JSON-serialisable request body. */
  body?: unknown;
  /** Abort signal. */
  signal?: AbortSignal;
  /**
   * Extra request headers (e.g. `Idempotency-Key` on a retry-safe POST, spec 07).
   * Applied first, so the fixed auth/JSON/XSRF headers below always win.
   */
  headers?: Readonly<Record<string, string>>;
}

/**
 * Perform a JSON API request against the v1 API.
 *
 * Resolves with the parsed `T` on success, or throws {@link ApiError} on any
 * non-2xx response. 204 responses resolve with `undefined`.
 */
export async function apiFetch<T>(path: string, options: ApiRequestOptions = {}): Promise<T> {
  // Caller headers first; Accept/Authorization/Content-Type/XSRF are set after,
  // so an extra header can never override (or downgrade) authentication.
  const headers = new Headers(options.headers ?? {});
  headers.set('Accept', 'application/json');
  const token = getToken();
  if (token !== null) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  const init: RequestInit = { method: options.method ?? 'GET', headers };
  if (options.body !== undefined) {
    headers.set('Content-Type', 'application/json');
    init.body = JSON.stringify(options.body);
  }
  // CSRF handshake for the stateful (Sanctum SPA) path: when the backend has set
  // an XSRF-TOKEN cookie, echo it as the X-XSRF-TOKEN header so a cookie-bearing
  // write is not rejected with 419. Harmless for the pure token path (no cookie →
  // no header). The cookie itself rides along via fetch's same-origin default.
  const method = (options.method ?? 'GET').toUpperCase();
  if (method !== 'GET' && method !== 'HEAD') {
    const xsrf = readCookie('XSRF-TOKEN');
    if (xsrf !== null) {
      headers.set('X-XSRF-TOKEN', xsrf);
    }
  }
  if (options.signal !== undefined) {
    init.signal = options.signal;
  }

  const response = await fetch(`${API_BASE}${path}`, init);

  if (response.status === 204) {
    return undefined as T;
  }

  const contentType = response.headers.get('content-type') ?? '';
  const isJson = contentType.includes('json');
  const payload: unknown = isJson ? await response.json() : null;

  if (!response.ok) {
    if (response.status === 401) {
      clearToken();
    }
    const problem = (payload ?? null) as ProblemDetails | null;
    throw new ApiError(response.status, problem, parseRetryAfter(response.headers.get('retry-after')));
  }

  return payload as T;
}
